import { ContactDetailsPatch, getChangeContactDetailsEndpoint, RequestStatus } from 'cpfr-common';
import {
    NotificationStatus,
    Spinner,
    useAnalyticsActionTracker,
    useAnalyticsFormTracker,
    useAnalyticsPageViewTracker,
    useOidcAuthentication,
    useOidcAuthProfile,
} from '@cp-shared-19/frontend-ui';
import { Notification } from '@vwfs-bronson/bronson-react';
import { withLoadingHandler } from 'components/integration-wrapper';
import { updateContactInformationChangeStatus, useMyProfile } from 'components/my-profile';
import { dashboardPagePath } from 'components/navigation/paths';
import { CpDataApi } from 'cp-xhr';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ContactDetailsCheckForm, ContactDetailsCheckFormFields } from './ui';
import { useDispatch } from 'react-redux';
import { OidcStandardClaims } from 'oidc-client-ts';

const ContactDetailsCheckWithHandlers = withLoadingHandler(ContactDetailsCheckForm);

type RequestProps = {
    isPending: boolean;
    isError: boolean;
};

export const ContactDetailsCheck: React.FC = () => {
    const { t } = useTranslation(['contact-details-check', 'loader']);
    const { data, isLoading } = useMyProfile();
    const [request, setRequest] = useState<RequestProps>({ isPending: false, isError: false });
    const auth = useOidcAuthentication();
    const profile = useOidcAuthProfile();
    const history = useHistory();
    const dispatch = useDispatch();
    useAnalyticsPageViewTracker('authenticationVerification', !!data);

    const { onSubmit: trackOnSubmit } = useAnalyticsFormTracker({
        confirm: 'onVerificationPageConfirm',
    });

    const { onAction: onSuccess } = useAnalyticsActionTracker('onVerificationPageSuccess');

    const handleSubmit = ({ email, mobilePhone }: ContactDetailsCheckFormFields): void => {
        const body: ContactDetailsPatch = {
            emailAddress: email,
            mobilePhone: mobilePhone.replace(/\s/g, ''),
        };
        setRequest({ isPending: true, isError: false });
        CpDataApi.patch(getChangeContactDetailsEndpoint(), body)
            .then(() => {
                onSuccess();
                dispatch(
                    updateContactInformationChangeStatus({
                        requestCustomerStatus: RequestStatus.IN_PROGRESS,
                    }),
                );
                history.push(dashboardPagePath());
            })
            .catch(() => {
                trackOnSubmit();
                setRequest({ isPending: false, isError: true });
            });
    };

    return (
        <>
            {request.isPending && (
                <Spinner center>
                    <p>{t('genericText', { ns: 'loader' })}</p>
                </Spinner>
            )}
            {request.isError && (
                <Notification
                    status={NotificationStatus.error}
                    className={'u-mb'}
                    title={''}
                    hideTitle={true}
                    visible
                    showCloseButton={false}
                >
                    {t('error.change-unsuccessful')}
                </Notification>
            )}
            <ContactDetailsCheckWithHandlers
                isLoading={isLoading}
                initPhone={data?.contactDetails.mobilePhone}
                initPrefix={'33'}
                handleSubmit={handleSubmit}
                fsidMail={auth.isAuthenticated ? (profile as OidcStandardClaims)?.email || '' : ''}
                profileMail={data?.contactDetails?.email}
            />
        </>
    );
};
